import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../../Spinner';
import {FaIcon} from '../../../FaIcon';
import {MERGE_PARTIAL, UNDO_DISCARDED, useClientFacts} from '../facts/ClientFactsStore';
import {findSimilar} from '../../memory/factSimilarity';
import {BOOSTER_STEP_FEED, BOOSTER_STEP_MEMORY} from '../boosterSteps';
import {scrollerFor} from '../flow/useSectionObserver';
import {ReviewCard} from './ReviewCard';
import {ReviewList} from './ReviewList';
import {ReviewRail} from './ReviewRail';
import {HINT_LOOKALIKE, HINT_UPDATE} from './hintKinds';
import {REVIEW_STATUS} from '../../memory/factConstants';
import {NoiseQueueSection} from './NoiseQueueSection';
import {ArchivedNoiseSection} from './ArchivedNoiseSection';
import {DuplicatesSection} from './DuplicatesSection';
import {useSourceViewer} from '../sourceViewerContext';
import {AutonomyRaiseOfferModal} from '../../memory/AutonomyRaiseOfferModal';
import {useAutonomyRaiseOffer} from '../../memory/useAutonomyRaiseOffer';
import {useKnobRefocus} from '../../memory/useKnobRefocus';
import {FLY_MS, NUB_FROM, REDUCED_FLY_MS, prefersReducedMotion} from './useSwipeCard';

/**
 * El acto REVISA: el mazo de cartas.
 *
 * La cola de curado de siempre son N filas con tres botones cada una; el mazo la convierte en una
 * decisión por vez, con el gesto, las flechas del teclado y una red de deshacer encadenada detrás.
 * No es cosmética: lo que hace que aprobar rápido no dé miedo es poder volver atrás, y lo que hace
 * que se revise es que cada decisión cueste un movimiento.
 *
 * Lo que este componente defiende, y que el mockup no:
 *
 * 1. **El teclado tiene tres guardas, no ninguna.** En el mockup ← y → siguen aprobando cartas
 *    invisibles detrás de un modal abierto: el usuario contesta "¿seguro?" con el teclado y de paso
 *    aprueba dos hechos que ni vio. Acá el teclado exige que el acto esté a la vista, que el foco no
 *    esté en un campo de texto y que no haya ningún modal abierto (`body.modal-open`, que es la
 *    clase que pone reactstrap).
 * 2. **La cola es UNA.** El mazo, la lista y el archivo leen `reviewQueue` del store; ninguna de las
 *    tres tiene su propia idea de qué está pendiente.
 * 3. **El ruido no pasa por el mazo.** Lo que el triage degradó es el TERCER modo de revisión, al
 *    lado de «De a uno» y «Lista completa», y sólo aparece si hay al menos uno (ver
 *    `NoiseQueueSection`).
 * 4. **El vacío no siempre significa lo mismo.** "Todo revisado" sólo se puede decir si hubo algo
 *    que revisar; si nunca entró material, lo honesto es decir eso y ofrecer el paso anterior.
 * 5. **Las tandas tienen el tope del backend, y cada tanda es UN paso.** El bulk rechaza más de
 *    `MAX_BULK` ids, así que "Aprobar todo" y el descarte por familia trabajan de a tandas — una
 *    acción que resuelve 200 y deja el resto es una acción que se puede terminar; una que falla con
 *    400 no se puede terminar nunca. Y cada tanda deja **un** paso de sesión por cada entrada de
 *    deshacer del store, del mismo tamaño (ver `runStep`): sin eso, un Z revertía un hecho y el
 *    rail restaba cuarenta.
 * 6. **Una tanda no puede convertirse en un fan-out.** Lo que no entra al bulk son las fusiones, y
 *    cada una son dos PATCH: acotadas (`MAX_MERGES_PER_BATCH`) y EN SERIE (`runJobsInSequence`),
 *    porque disparar 400 requests de una es justo lo que el endpoint bulk existe para evitar.
 */

const MODE_DECK = 'deck';
const MODE_LIST = 'list';
// «Ruido probable» es un MODO, no una sección al pie. Vivía debajo del mazo, colapsado, y adentro
// cada familia se abría con un segundo click: dos clicks para ver por qué el sistema no le dio
// entidad a lo que acabás de subir. Pablo, 10/09: «siento que quedó como muy separado de todo esto
// y me escondido con un doble click». Como modo, se ve desde el mismo control que elige cómo
// revisar —el usuario ya lo mira para decidir mazo o lista— y cuesta UN click. Lo que no cambia es
// que el ruido sigue fuera de la cola principal: ni entra al mazo, ni al rail, ni al avance.
const MODE_NOISE = 'noise';

/** Cuánto tarda cada carta en salir durante "Aprobar todo". El mockup usa este mismo ritmo. */
export const CASCADE_MS = 110;

/**
 * Cuántas cartas vuelan como mucho en la cascada de "Aprobar todo".
 *
 * La cascada es un GESTO —"mirá, salieron todas"—, no una barra de progreso: con la tanda entera
 * a 110ms por carta, 200 hechos son 22 segundos de animación **antes** del primer request, con la
 * pantalla trabada y sin forma de cancelar. Vuelan las primeras y el resto se resuelve de una.
 */
export const MAX_CASCADE = 6;

/**
 * Cuántos hechos entran en UNA tanda.
 *
 * Espeja `MAX_BULK_FACTS` de `agent_tools/views/client_fact_views.py`: el endpoint bulk devuelve
 * **400** si le llegan más ids, y el listado trae hasta 500 hechos, así que el tope es alcanzable
 * con una cola real. Sin acotar acá, "Aprobar todo" con 250 pendientes falla siempre —y reintentar
 * falla igual, para siempre—; acotado, resuelve 200 y deja el resto en la cola para la tanda
 * siguiente, que es una acción que el usuario puede terminar.
 */
export const MAX_BULK = 200;

/**
 * Cuántas FUSIONES entran en una tanda de «Aprobar todo».
 *
 * Los hechos con hint de duplicado no pueden ir al bulk (aprobarlos a secas dejaría en la memoria el
 * viejo y el nuevo diciendo lo mismo), así que van de a uno por `mergeAndApprove` — y cada uno es
 * **dos** PATCH. Sin tope, el peor caso de una tanda de 200 pendientes todos con duplicado son 400
 * PATCH disparados de golpe: 200 transacciones sueltas, 400 filas de Bitácora y 200 encolados de
 * embedding, que es exactamente lo que el endpoint bulk existe para evitar.
 *
 * No es un caso de laboratorio: `findSimilar` es un heurístico de overlap de palabras, y un cliente
 * con material repetido —varios briefs de la misma marca— es justo donde el hint dispara seguido.
 *
 * 20 es el número que hace que la tanda siga siendo una acción que se termina: acotado ASÍ, un click
 * son como mucho 1 bulk + 40 PATCH, y lo que sobra queda en la cola con el botón habilitado, igual
 * que con el tope de 200 del bulk. Las fusiones además van SERIALIZADAS (ver `runJobsInSequence`):
 * el tope acota el trabajo total del click y la serie acota lo que el backend recibe a la vez.
 */
export const MAX_MERGES_PER_BATCH = 20;

/** Cuántas cartas se apilan a la vista. Más es ruido; menos no se lee como un mazo. */
const STACK = 3;

/**
 * Qué resuelve UN click de «Aprobar todo»: los que van al bulk, los que van por fusión, y el orden.
 *
 * Los dos topes son distintos y por eso se cuentan aparte (`MAX_BULK` es el del endpoint bulk;
 * `MAX_MERGES_PER_BATCH` es el del fan-out de PATCH). `order` sigue el orden de la COLA, no el de
 * los grupos: es el que usa la cascada para hacer volar las cartas de arriba, y con el orden de los
 * grupos animaría una carta que no está en la pila.
 *
 * Es también el único lugar donde se pregunta `similarOf` por hecho. Antes se lo llamaba tres veces
 * —dos filtros y el `forEach`— para el mismo resultado.
 */
/** De dónde salió el hint de una carta: lo señaló el backend (`update_of_id`) o es parecido léxico. */
export {HINT_LOOKALIKE, HINT_UPDATE};

/**
 * El hecho que este pendiente ACTUALIZA, según el reconciliador del backend (`update_of_id`), o
 * null. Tiene precedencia sobre el parecido léxico: el árbitro leyó los dos statements y dijo
 * «mismo sujeto, otro valor», que es justo lo que el Jaccard no puede ver («80» vs «100»).
 *
 * Se descarta si el señalado ya no está en el store, ya fue reemplazado (`superseded_by_id`) o no
 * está en la BIBLIOTECA (aprobado y activo): `findSimilar` siempre apuntó a un aprobado, y «Fusionar
 * y aprobar» hace `supersede_by_id` sobre él — contra un pendiente («Revisar» desde «Entró solo») o
 * un rechazado del archivo el backend lo rechaza y el Deshacer queda muerto (C-memoria-12).
 */
export function updateTargetOf(fact, byId) {
    if (!fact || !fact.update_of_id) return null;
    const other = (byId || {})[fact.update_of_id];
    if (!other || other.id === fact.id || other.superseded_by_id) return null;
    if (other.review_status !== REVIEW_STATUS.APPROVED || !other.is_active) return null;
    return other;
}

/**
 * Cómo se reparte la tanda que ofreció el knob: qué va al bulk y qué va por fusión.
 *
 * Es una función pura y exportada para poder probarla: vivía adentro del componente y por eso no
 * tenía guardián (C-knob2-06), justo la parte donde se concentran dos hallazgos.
 *
 * Dos diferencias con `approveAllBatchOf`, que es la de «Aprobar todo»:
 *
 * 1. Recibe **ids**, no hechos — los de la oferta salen de un GET del backend.
 * 2. Los ids que el store NO tiene cargados van al bulk como `plain` en vez de descartarse
 *    (C-knob2-02): el store trae 500 pendientes y la previsualización escanea 1.000, así que en la
 *    cola pesada —el caso que motiva la oferta— parte de los ids no está en memoria. Descartarlos era
 *    una regresión: antes del frente esos hechos se aprobaban, y si no quedaba ninguno conocido la
 *    oferta contestaba «no pudimos aprobar» y el reintento no servía nunca. Sin el hecho en la mano no
 *    se puede preguntar por su duplicado, y el bulk es exactamente lo que el backend hace con ellos.
 */
export function offeredBatchOf(factIds, facts, similarOf) {
    const byId = new Map(facts.map(fact => [fact.id, fact]));
    const known = [];
    const unknownIds = [];
    factIds.forEach((id) => {
        const fact = byId.get(id);
        if (fact) known.push(fact); else unknownIds.push(id);
    });
    const {plain, merges} = approveAllBatchOf(known, similarOf);
    return {ids: [...plain.map(fact => fact.id), ...unknownIds], merges};
}

export function approveAllBatchOf(queue, similarOf) {
    const plain = [];
    const merges = [];
    const order = [];
    queue.forEach((fact) => {
        const duplicate = similarOf(fact);
        if (duplicate) {
            if (merges.length >= MAX_MERGES_PER_BATCH) return;
            merges.push({fact, duplicate});
        } else {
            if (plain.length >= MAX_BULK) return;
            plain.push(fact);
        }
        order.push(fact.id);
    });
    return {plain, merges, order};
}

const TOAST_ID = 'iab-review-action';
const TOAST_MS = 5000;
/** Cuánto dura la marca de "ese gesto no se aplicó". Corta a propósito: es un acuse, no un aviso. */
const IGNORED_MS = 420;

/** El foco está en algo que escribe: el teclado del mazo no existe mientras tanto. */
function isTypingTarget(el) {
    if (!el) return false;
    const tag = (el.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return el.isContentEditable === true;
}

// Los `input` que de verdad ESCRIBEN. El resto —el riel del knob, un checkbox, un botón— son
// controles: se llevan sus propias teclas (las flechas de un `range`), pero no `Ctrl+Z`.
const TEXT_INPUT_TYPES = [
    'text', 'search', 'email', 'url', 'tel', 'password', 'number', 'date', 'datetime-local',
    'month', 'week', 'time',
];

/**
 * ¿El foco está en un campo de TEXTO? Un campo de texto se lleva **todo** el teclado, incluido el
 * deshacer, que ahí es el del navegador. Un control que no escribe, no: si no, devolverle el foco al
 * riel del knob al cerrar su modal dejaba el `Ctrl+Z` del mazo muerto hasta que el usuario clickeara
 * en otro lado (C-knob3-02, review del 27/09 — el test lo admitía haciendo `blur()` antes del Z).
 */
function isTextEntry(el) {
    if (!el) return false;
    const tag = (el.tagName || '').toUpperCase();
    if (tag === 'TEXTAREA') return true;
    if (tag === 'INPUT') {
        return TEXT_INPUT_TYPES.includes((el.getAttribute('type') || 'text').toLowerCase());
    }
    return el.isContentEditable === true;
}

export function BoosterReviewDeck({clientId, keyboardEnabled, onGoToMemory,
                                   extractionActive = false, processingUnknown = false,
                                   autonomy, onAutonomyChange, autonomySaving}) {
    const {t} = useTranslation();
    const {
        facts, documentsById, loading, loadFailed, isBusy, undoDepth,
        reviewQueue, noiseQueue, archivedQueue, archivedFailed, archivedHidden,
        library, pendingCount, actions,
    } = useClientFacts();

    const [mode, setMode] = useState(MODE_DECK);
    const [editingId, setEditingId] = useState(null);
    // La carta que está volando (y hacia dónde). Sólo puede haber una: mientras vuela, el mazo no
    // acepta otra decisión — si no, dos flechas seguidas resuelven la misma carta dos veces.
    const [exiting, setExiting] = useState(null);
    // Ids ya "volados" por la cascada de Aprobar todo. La cola real no cambia hasta que responde el
    // bulk, así que sin esto la pila se quedaría quieta mientras las cartas salen.
    const [hidden, setHidden] = useState([]);
    // Hints de duplicado que el usuario descartó con "Separar". Son locales: el hint lo calcula el
    // frontend por parecido de palabras, no es una sugerencia persistida del backend.
    const [split, setSplit] = useState([]);
    // `noise`: los descartes por grupo de «Ruido probable». Espejan la pila de deshacer igual que
    // los otros dos (por eso pasan por `runStep`), pero NO entran en la barra ni en el desglose: la
    // barra mide decisiones de a una sobre la cola principal, y un grupo de 40 ruidos descartado de
    // un click la inflaba a "93 % de avance" sin haber decidido una sola carta.
    // (Hubo un cuarto, `removed`: los «Quitar» de «Entró solo». Esa sección se integró al archivo
    // del acto Memoria el 2026-09-08 y con ella se fue su único escritor.)
    const [session, setSession] = useState({approved: 0, rejected: 0, noise: 0});
    // «Ver la cita» sobre la fuente de una carta: abre el visor ÚNICO de la página
    // (`SourceViewerHost`), posicionado en la cita del hecho. Hasta el 2026-09-12 el mazo montaba
    // su propio `DocumentRawModal`, que abría el crudo desde arriba; hoy es el mismo visor que usan
    // el archivo de Memoria y el ruido archivado. Sin provider no se ofrece: la carta no dibuja un
    // botón que no abre nada.
    const viewer = useSourceViewer();
    const openSource = useCallback(
        (doc, fact) => viewer.openSource(doc, {quote: fact && fact.source_quote}),
        [viewer]);
    const onOpenSource = viewer.available ? openSource : undefined;
    const [inBand, setInBand] = useState(true);
    // La oferta de aprobar lo que ya estaba esperando, al SUBIR la autonomía (card 8uu3IdNd).
    const autonomyOffer = useAutonomyRaiseOffer(clientId);
    // El riel del knob, para devolverle el foco cuando su modal se cierra — sólo si el usuario lo
    // movió con el TECLADO. El helper es uno para las dos superficies: ver `useKnobRefocus`.
    const {knobRef, refocusKnob, rememberOrigin} = useKnobRefocus();

    const rootRef = useRef(null);
    // El primer boton del switch. Es el destino del foco cuando el modo ruido se vacia solo: ver
    // el efecto de la vuelta automatica.
    const deckModeRef = useRef(null);
    const timerRef = useRef(null);
    const exitingRef = useRef(false);
    // Señal de "tu gesto llegó tarde": la carta anterior todavía está volando, así que la decisión
    // no se aplica. No produce un estado falso —los contadores nunca mintieron—, pero sin señal el
    // usuario aprieta y no pasa nada. Es una marca efímera en el mazo, no un toast: encadenar
    // teclas es normal y un aviso modal por cada una sería peor que el silencio.
    const [ignoredAt, setIgnoredAt] = useState(0);
    const ignoredTimerRef = useRef(null);
    const nubOkRef = useRef(null);
    const nubNoRef = useRef(null);
    const actionsRef = useRef(actions);
    const undoDepthRef = useRef(undoDepth);
    const notifyRef = useRef(null);
    // Un deshacer en vuelo. No es `useState` a proposito: lo lee el handler del toast, que se
    // renderiza una sola vez y no ve los renders siguientes.
    const undoingRef = useRef(false);
    // Qué fue cada paso de la sesión (y cuántos hechos movió), para que deshacer devuelva el
    // contador a donde estaba en vez de dejarlo inflado. **Hay exactamente un paso por cada
    // entrada de la pila de deshacer del store, del mismo tamaño y en el mismo orden** — ver
    // `runStep`.
    const sessionStackRef = useRef([]);
    // Id propio de cada paso, por el mismo motivo que el `undoId` del store: cuando contesta el
    // servidor, el paso que empujó esta operación puede tener otros encima, así que descontarlo
    // "por posición" descontaría el ajeno.
    const stepSeqRef = useRef(0);

    actionsRef.current = actions;
    undoDepthRef.current = undoDepth;

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    // El botón de «Ruido probable» sólo existe si hay ruido, así que el modo tiene que poder
    // quedarse sin su botón: pasa al descartar el último grupo, y también sin que el usuario toque
    // nada (el triage archiva familias solo). Vuelve al mazo — dejarlo ahí sería un panel vacío
    // bajo un control que ya no dice dónde estás. No vuelve solo al ruido si más tarde entra otro:
    // el modo lo elige el usuario.
    // ⚠️ Limpia `editingId` ADEMÁS de cambiar el modo, y no es opcional: si el ruido se vacía, lo
    // que se estaba editando era un hecho de ruido que ya no se dibuja en ningún panel. Sin esta
    // línea el editor queda abierto e invisible y el teclado del mazo se apaga con él (ver
    // `changeMode`), o sea el «mazo mudo» que este mismo commit vino a arreglar, entrando por la
    // puerta de al lado: «Editar» en una fila de ruido → «Descartar grupo» (ese botón NO está
    // gateado por `editingId`) → vuelta automática con la edición todavía abierta. Lo encontró el
    // review del 10/09 (C-revisa-01); los dos guardianes que había no lo cubrían porque uno no
    // abre editor y el otro cambia de modo a mano.
    useEffect(() => {
        if (mode === MODE_NOISE && noiseQueue.length === 0) {
            setEditingId(null);
            setMode(MODE_DECK);
            // Y el foco se muda al switch. No es robarselo a nadie: en este camino el foco YA se
            // perdio —el modal de confirmacion lo devuelve al boton «Descartar grupo», que acaba de
            // desaparecer, y el boton del tercer modo se fue con el—, asi que sin esto termina en
            // `<body>` y a un lector de pantalla el panel entero le cambio sin una sola señal.
            // Se mueve al control que explica DONDE quedaste, que es el switch. Review 10/09
            // (C-revisa-06). Sólo si el foco esta perdido: si el usuario ya lo puso en otro lado
            // mientras tanto, no se lo movemos.
            const perdido = !document.activeElement || document.activeElement === document.body;
            if (perdido && deckModeRef.current) deckModeRef.current.focus();
        }
    }, [mode, noiseQueue.length]);

    // --- visibilidad (sólo como FALLBACK) -----------------------------------------------------
    // Si el que monta el mazo ya sabe qué acto se está mirando, manda su `keyboardEnabled`. Si no
    // llega nada, el mazo se entera solo: el teclado tiene que estar apagado mientras el usuario
    // está scrolleando otro acto de la misma página.
    useEffect(() => {
        if (keyboardEnabled !== undefined) return undefined;
        const node = rootRef.current;
        if (!node || typeof window.IntersectionObserver !== 'function') return undefined;
        const scroller = scrollerFor(node);
        const observer = new window.IntersectionObserver((entries) => {
            entries.forEach(entry => setInBand(entry.isIntersecting));
        }, {root: scroller || null, rootMargin: '-25% 0px -25% 0px', threshold: 0});
        observer.observe(node);
        return () => observer.disconnect();
    }, [keyboardEnabled]);

    const keyboardActive = keyboardEnabled === undefined ? inBand : keyboardEnabled !== false;

    // --- la cola visible y sus duplicados ------------------------------------------------------

    const visible = useMemo(
        () => reviewQueue.filter(f => !hidden.includes(f.id)),
        [reviewQueue, hidden],
    );

    // Hint anti-duplicados: para cada pendiente, el hecho aprobado más parecido.
    //
    // ⚠️ El memo va sobre `facts`, NO sobre `reviewQueue`/`library`: esas dos las **deriva** el
    // provider dentro de su `useMemo`, así que son arrays nuevos en cada cambio de estado del
    // store —incluido el par de `BUSY` que abre y cierra cada mutación—. `state.facts`, en cambio,
    // conserva su referencia salvo que un hecho cambie de verdad. Con 40 pendientes y 300
    // aprobados, `findSimilar` tokeniza la biblioteca entera por pendiente: eran ~12k
    // tokenizaciones por aprobación, cuatro veces, por un resultado idéntico.
    //
    // Dos orígenes, un solo mapa: si el backend señaló un `update_of_id` (el árbitro dijo «mismo
    // sujeto, otro valor»), ese hecho gana sobre el parecido léxico; `hintKindById` guarda cuál
    // fue, porque la carta lo dice distinto («Cambió» vs «Posible duplicado») y se resuelve igual
    // (fusionar = aprobar el nuevo y reemplazar al viejo).
    const {similarById, hintKindById} = useMemo(() => {
        const map = {};
        const kinds = {};
        const byId = {};
        facts.forEach((f) => { byId[f.id] = f; });
        reviewQueue.forEach((fact) => {
            const updated = updateTargetOf(fact, byId);
            const match = updated || findSimilar(fact, library);
            if (match) {
                map[fact.id] = match;
                kinds[fact.id] = updated ? HINT_UPDATE : HINT_LOOKALIKE;
            }
        });
        return {similarById: map, hintKindById: kinds};
        // `reviewQueue` y `library` son función pura de `facts` (los selectores del store): si
        // `facts` no cambió, su CONTENIDO es el mismo aunque la referencia no lo sea.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facts]);

    const similarOf = useCallback(
        (fact) => (fact && !split.includes(fact.id) ? similarById[fact.id] : null),
        [similarById, split],
    );

    // Sólo para el copy de la carta: «Son distintos» apaga el hint entero, origen incluido.
    const similarKindOf = useCallback(
        (fact) => (fact && !split.includes(fact.id) ? hintKindById[fact.id] : null),
        [hintKindById, split],
    );

    // La tanda que resuelve «Aprobar todo», calculada UNA vez: la usan el botón (para decir cuántos
    // son antes de confirmar) y la acción (para resolver exactamente esos). Si el número del rail y
    // el de la acción se calcularan por separado, la confirmación podría prometer otra cosa.
    const approveAllBatch = useMemo(
        () => approveAllBatchOf(visible, similarOf), [similarOf, visible]);

    const stack = visible.slice(0, STACK);
    const top = stack[0] || null;

    // ¿El hecho que se está editando se va a DIBUJAR en el panel destino? El editor es uno solo
    // (`editingId`) y los tres modos se lo turnan; el teclado del mazo se apaga con `editingId` a
    // secas —a propósito: mientras alguien escribe, las flechas no vuelan cartas—, así que un
    // editor que sobrevive a un cambio de modo sin poder verse deja el mazo mudo y sin nada en
    // pantalla que lo explique.
    //
    // Pero cerrarlo SIEMPRE tampoco sirve: el mazo y la lista leen la MISMA `reviewQueue`, así que
    // pasar de «De a uno» a «Lista completa» mientras se escribe borraba lo tipeado en silencio
    // (review del 10/09, C-revisa-05). La pregunta correcta no es «cambiaste de modo» sino «¿se
    // sigue viendo?».
    //
    // Ojo con el mazo: dibuja `stack`, que son las primeras `STACK` cartas, NO la cola entera.
    // Editar el hecho 7 en la lista y volver al mazo también lo dejaba fuera de pantalla — la
    // tercera puerta al mismo defecto, que un `mode !== destino` no habría cerrado.
    const editingVisibleIn = (target) => {
        if (!editingId) return false;
        if (target === MODE_DECK) return stack.some(f => f.id === editingId);
        if (target === MODE_LIST) return visible.some(f => f.id === editingId);
        return noiseQueue.some(f => f.id === editingId);
    };

    const changeMode = (next) => {
        if (!editingVisibleIn(next)) setEditingId(null);
        setMode(next);
    };
    // El mazo se apaga sólo si lo que se está editando es una carta SUYA. La cola de ruido vive
    // fuera del mazo: editar una de sus filas no tiene por qué congelar el gesto ni los nubs.
    const deckEditing = !!editingId && stack.some(f => f.id === editingId);

    // --- avisos ---------------------------------------------------------------------------------

    const handleUndo = useCallback(() => {
        // Sin pila no hay nada que deshacer: llamar igual devolvería `false` y el usuario vería un
        // error por haber apretado un botón que no debía hacer nada.
        if (undoDepthRef.current <= 0) return;
        const left = Math.max(0, undoDepthRef.current - 1);
        const step = sessionStackRef.current.pop();
        // El aviso pasa a «Deshaciendo…» ANTES de disparar (QA 2026-09-07, sugerencia de
        // QA-cola-06): deshacer un «Quitar» son dos PATCH encadenados mas el refetch —del orden de
        // cinco segundos—, y sin esto el usuario se queda mirando una lista que no se mueve, con el
        // boton todavia ofreciendo lo que ya apreto. Se apaga solo: todos los caminos de salida
        // (exito y los dos errores) escriben el aviso de nuevo.
        if (undoingRef.current) return;   // dos clics no disparan dos veces la misma pila
        undoingRef.current = true;
        if (notifyRef.current) notifyRef.current(t('ia_booster.deck.undoing'), false);
        actionsRef.current.undoLast().then((result) => {
            undoingRef.current = false;
            if (result !== true) {
                // El paso vuelve a la pila SÓLO si el store devolvió su entrada. Cuando la descarta
                // —falló las veces suficientes y estaba bloqueando a todas las de abajo— el cambio
                // sigue en pie y ya no tiene vuelta atrás: devolver el paso dejaría un paso de
                // sesión sin entrada que lo espeje, que es justo lo que `runStep` sostiene. Y el
                // aviso lo dice, porque "no se pudo" invita a apretar Z otra vez.
                const dropped = result === UNDO_DISCARDED;
                if (step && !dropped) sessionStackRef.current.push(step);
                toast.error(t(dropped
                    ? 'ia_booster.deck.undo_dropped'
                    : 'ia_booster.deck.error'));
                return;
            }
            if (step) {
                setSession(prev => ({
                    ...prev,
                    [step.kind]: Math.max(0, prev[step.kind] - step.n),
                }));
            }
            setHidden([]);
            const message = left <= 0
                ? t('ia_booster.deck.toast_undone_none')
                : (left === 1
                    ? t('ia_booster.deck.toast_undone_one')
                    : t('ia_booster.deck.toast_undone', {n: left}));
            if (notifyRef.current) notifyRef.current(message, false);
        });
    }, [t]);

    /**
     * El aviso de cada decisión, con "Deshacer" adentro.
     *
     * ⚠️ Un `toast()` con un `toastId` ya activo se descarta EN SILENCIO (react-toastify 11): en un
     * mazo, donde el caso normal es decidir varias cartas seguidas, eso significa que el segundo
     * aviso —y su botón de deshacer— simplemente no existe. De ahí el `isActive` + `update`.
     */
    const notify = useCallback((message, withUndo = true) => {
        const content = (
            <div className="iabr-toast" role="status" aria-live="polite">
                <span className="iabr-toast-msg">{message}</span>
                {withUndo && (
                    <button type="button" className="iabr-toast-undo" onClick={handleUndo}>
                        {t('ia_booster.deck.undo')}
                    </button>
                )}
            </div>
        );
        if (toast.isActive(TOAST_ID)) toast.update(TOAST_ID, {render: content, autoClose: TOAST_MS});
        else toast(content, {toastId: TOAST_ID, autoClose: TOAST_MS});
    }, [handleUndo, t]);

    notifyRef.current = notify;

    const pushStep = useCallback((kind, n) => {
        stepSeqRef.current += 1;
        const step = {id: stepSeqRef.current, kind, n};
        sessionStackRef.current.push(step);
        setSession(prev => ({...prev, [kind]: prev[kind] + n}));
        return step;
    }, []);

    const dropStep = useCallback((step) => {
        sessionStackRef.current = sessionStackRef.current.filter(s => s.id !== step.id);
        setSession(prev => ({...prev, [step.kind]: Math.max(0, prev[step.kind] - step.n)}));
    }, []);
    /**
     * Corrige el TAMAÑO de un paso ya empujado (C-knob2-01, review del 26/09).
     *
     * El paso se empuja antes de que conteste el servidor, con el tamaño que se le pidió. Con
     * `skip_missing` el backend puede mover menos, y el store recorta su entrada de Deshacer a lo que
     * de verdad se movió. Si el paso no se recorta con ella, se rompe la invariante que sostiene el
     * rail —*un paso por entrada, del mismo tamaño*— y un `Ctrl+Z` resta 45 aprobados revirtiendo 44.
     */
    const amendStep = useCallback((step, n) => {
        const delta = n - step.n;
        if (!delta) return;
        step.n = n;
        setSession(prev => ({...prev, [step.kind]: Math.max(0, prev[step.kind] + delta)}));
    }, []);

    /**
     * Un paso de sesión por CADA entrada de deshacer, del MISMO tamaño y en el MISMO orden.
     *
     * Es la única forma de que un Z deje los números coherentes. Antes no lo era: descartar un
     * grupo de ruido de 40 metía 40 entradas de deshacer (un `reject` por hecho) contra UN paso de
     * sesión con `n = 40`, así que el primer Z revertía un hecho y el rail restaba los cuarenta —el
     * desglose pasaba a "0 rechazados" con 39 todavía rechazados, y el aviso decía "quedan 39
     * cambios para deshacer"—. Y "Aprobar todo" dejaba `1 (bulk) + N (fusiones)` entradas contra un
     * solo paso.
     *
     * ⚠️ La acción del store se llama ANTES (el `promise` ya está en vuelo cuando entra acá): tanto
     * `mutate` como `bulkReview` empujan su entrada de deshacer de forma síncrona, así que llamar
     * la acción y empujar el paso en ese orden mantiene las dos pilas alineadas.
     */
    const runStep = useCallback((kind, n, promise, realCount) => {
        const step = pushStep(kind, n);
        return promise.then((result) => {
            // El paso de sesión sigue EXACTAMENTE a la entrada de deshacer del store: se va sólo
            // cuando el store sacó la suya, que es lo que significa el `false`. Una fusión a medias
            // (`MERGE_PARTIAL`) deja la suya —el pendiente quedó aprobado de verdad—, así que el
            // paso también se queda: soltarlo dejaría un Z restando un cambio que el rail no contó.
            if (result === false) {
                dropStep(step);
                return result;
            }
            // `realCount` (opcional) lo pasa quien pidió tolerancia: el store ya recortó su entrada
            // de Deshacer a los hechos que se movieron, y el paso tiene que acompañarla.
            //
            // ⚠️ Con CERO movidos el store no recorta su entrada: la TIRA (`DROP_UNDO`). Un paso de
            // `n = 0` que sobrevive a eso deja una pila más larga que la otra, y el siguiente Z
            // —desde el «Deshacer» del aviso o con Ctrl+Z— saca ese paso vacío y revierte la entrada
            // de ABAJO, que es la decisión anterior del usuario. Misma familia que QA-knob2-05, por
            // la puerta de «el backend salteó todo».
            if (realCount) {
                const real = realCount();
                if (real > 0) amendStep(step, real);
                else dropStep(step);
            }
            return result;
        });
    }, [amendStep, dropStep, pushStep]);

    /**
     * Los jobs de una tanda, UNO POR VEZ. Devuelve cuántos hechos entraron de verdad.
     *
     * `Promise.all` los lanzaba todos juntos, y con las fusiones eso son 2 PATCH por hecho sin
     * ningún límite de concurrencia. Encadenarlos deja como mucho un request de esta acción en
     * vuelo, que es la mitad del problema que le toca al backend (la otra la acota
     * `MAX_MERGES_PER_BATCH`, que es cuánto trabajo total puede pedir un click).
     *
     * ⚠️ El job se ARRANCA acá adentro (`job.start()`), no antes: `runStep` empuja su paso de sesión
     * en el mismo momento en que la acción del store empuja su entrada de deshacer, y las dos pilas
     * tienen que quedar en el mismo orden (ver el comentario de `runStep`). Arrancarlos todos por
     * adelantado y encadenar sólo la espera sería el mismo fan-out con otra forma.
     */
    const runJobsInSequence = useCallback((jobs) => jobs.reduce(
        (chain, job) => chain.then(entered => runStep('approved', job.n, job.start(), job.real)
            .then((result) => {
                if (result === false) return entered;
                // Con tolerancia, lo que entró no es el tamaño del job: `job.real` lo sabe recién
                // cuando el backend contestó cuántos salteó.
                return entered + (job.real ? job.real() : job.n);
            })),
        Promise.resolve(0),
    ), [runStep]);

    // --- decisiones ------------------------------------------------------------------------------

    /** Enciende la marca de "gesto ignorado" y la apaga sola. Reiniciable: apretar dos veces
     *  seguidas vuelve a arrancar el reloj en vez de dejar la marca a medio camino. */
    const signalIgnored = useCallback(() => {
        setIgnoredAt(Date.now());
        if (ignoredTimerRef.current) clearTimeout(ignoredTimerRef.current);
        ignoredTimerRef.current = setTimeout(() => setIgnoredAt(0), IGNORED_MS);
    }, []);

    useEffect(() => () => {
        if (ignoredTimerRef.current) clearTimeout(ignoredTimerRef.current);
    }, []);

    /**
     * Corre `run` después de que la carta salga de pantalla. El orden importa: si la acción se
     * ejecutara primero, el store sacaría el hecho de la cola en el acto (es optimista) y la carta
     * desaparecería de golpe, sin volar. Con `prefers-reduced-motion` no hay vuelo, sólo un fade.
     */
    const flyAndRun = useCallback((fact, direction, run) => {
        if (exitingRef.current) { signalIgnored(); return; }
        exitingRef.current = true;
        setExiting({id: fact.id, dir: direction});
        timerRef.current = setTimeout(() => {
            exitingRef.current = false;
            setExiting(null);
            run();
        }, prefersReducedMotion() ? REDUCED_FLY_MS : FLY_MS);
    }, []);

    // Sin guard por `editingId`: quién puede decidir mientras se edita ya está resuelto donde
    // corresponde (el teclado lo chequea, los nubs se deshabilitan y la carta deja de recibir el
    // gesto). Ponerlo también acá rompía justo el caso que sí tiene que pasar —guardar la edición
    // aprueba— porque `setEditingId(null)` todavía no se aplicó cuando se llama.
    const decide = useCallback((fact, direction, run) => {
        if (!fact) return;
        if (mode === MODE_DECK) flyAndRun(fact, direction, run);
        else run();
    }, [flyAndRun, mode]);

    // Las versiones "ya" no vuelan ninguna carta: las usa la sección de ruido, cuyas filas viven
    // fuera del mazo. Animar la salida de una carta que no está en la pila trabaría el mazo 330ms
    // por una acción que ocurrió en otro lado.
    const approveNow = useCallback((fact) => {
        runStep('approved', 1, actions.approve(fact.id)).then((ok) => {
            if (!ok) {
                toast.error(t('ia_booster.deck.error'));
                return;
            }
            notifyRef.current(t('ia_booster.deck.toast_approved'));
        });
    }, [actions, runStep, t]);

    const rejectNow = useCallback((fact) => {
        runStep('rejected', 1, actions.reject(fact.id)).then((ok) => {
            if (!ok) {
                toast.error(t('ia_booster.deck.error'));
                return;
            }
            notifyRef.current(t('ia_booster.deck.toast_rejected'));
        });
    }, [actions, runStep, t]);

    // Desde «Archivado solo»: el hecho vuelve a la cola principal. No es un paso de sesión (no
    // aprueba ni rechaza) y el store no empuja entrada de deshacer, así que tampoco pasa por
    // `runStep`, igual que «Revisar».
    const restoreNow = useCallback((fact) => {
        actions.unarchive(fact.id).then((ok) => {
            if (!ok) {
                toast.error(t('ia_booster.deck.error'));
                return;
            }
            // CON «Deshacer», igual que «Quitar»: son dos botones vecinos y comportarse distinto es
            // lo que hace desconfiar de los dos (decisión del 2026-09-06).
            notifyRef.current(t('ia_booster.archived.toast_restored'));
        });
    }, [actions, t]);

    const handleApprove = useCallback(
        (fact) => decide(fact, 'right', () => approveNow(fact)), [approveNow, decide]);

    const handleReject = useCallback(
        (fact) => decide(fact, 'left', () => rejectNow(fact)), [decide, rejectNow]);

    const handleMerge = useCallback((fact, duplicate) => decide(fact, 'right', () => {
        runStep('approved', 1, actions.mergeAndApprove(fact.id, duplicate.id)).then((result) => {
            if (result === false) {
                toast.error(t('ia_booster.deck.error'));
                return;
            }
            // Una fusión a medias no fusionó nada: el hecho entró, pero el viejo sigue en la
            // memoria diciendo lo mismo. Decir "duplicados fusionados" ahí es la mentira que hace
            // que nadie vaya a mirar.
            notifyRef.current(result === MERGE_PARTIAL
                ? t('ia_booster.deck.toast_merged_partial')
                : t('ia_booster.deck.toast_merged'));
        });
    }), [actions, decide, runStep, t]);

    // "Separar": el parecido era casualidad. No hay nada que guardar —el hint lo calcula esta
    // pantalla— así que la carta pasa a resolverse como cualquier otra.
    const handleSplit = useCallback((fact) => {
        setSplit(prev => (prev.includes(fact.id) ? prev : [...prev, fact.id]));
    }, []);

    const runSaveEdit = useCallback((fact, statement) => {
        const duplicate = similarOf(fact);
        // Con duplicado, editar y aprobar ES la fusión con el texto que quedó: el hecho viejo
        // tiene que quedar superseded igual, o la memoria se queda con los dos.
        const done = duplicate
            ? actions.mergeAndApprove(fact.id, duplicate.id, statement)
            : actions.editAndApprove(fact.id, statement);
        runStep('approved', 1, done).then((result) => {
            if (result === false) {
                toast.error(t('ia_booster.deck.error'));
                return;
            }
            notifyRef.current(result === MERGE_PARTIAL
                ? t('ia_booster.deck.toast_merged_partial')
                : t('ia_booster.deck.toast_approved'));
        });
    }, [actions, runStep, similarOf, t]);

    const handleSaveEdit = useCallback((fact, statement) => {
        setEditingId(null);
        decide(fact, 'right', () => runSaveEdit(fact, statement));
    }, [decide, runSaveEdit]);

    // Guardar una edición de la cola de ruido no vuela ninguna carta: esa fila vive fuera del mazo,
    // igual que su aprobar y su rechazar (ver `approveNow` / `rejectNow`).
    const handleSaveEditNow = useCallback((fact, statement) => {
        setEditingId(null);
        runSaveEdit(fact, statement);
    }, [runSaveEdit]);

    // --- "Aprobar todo" ---------------------------------------------------------------------------

    /**
     * El knob y su oferta retroactiva (card 8uu3IdNd, punto 4).
     *
     * El nivel se guarda igual que siempre —eso lo hace el hook del padre— y RECIÉN si el guardado
     * entró de verdad se le pregunta al backend cuántos de los que ya esperan habrían entrado solos
     * con la posición nueva. Ofrecer sobre un PUT fallido sería proponer aprobar una tanda por un
     * criterio que no está puesto; por eso `setValue` resuelve a un booleano.
     *
     * `autonomy.effective` se lee ANTES de guardar: después ya es el nivel nuevo, y sin el anterior
     * no se puede saber si el usuario subió o bajó.
     */
    const handleAutonomyChange = useCallback((next, meta) => {
        const previous = autonomy && autonomy.effective;
        rememberOrigin(meta);
        return Promise.resolve(onAutonomyChange(next))
            .then((saved) => (saved ? autonomyOffer.askAfterChange(previous, next) : undefined));
    }, [autonomy, onAutonomyChange, autonomyOffer, rememberOrigin]);

    /**
     * Cascada visual + una tanda ACOTADA, resuelta de a un request por vez.
     *
     * Tres topes, por tres motivos distintos:
     *
     * - `MAX_BULK` es el del backend. Sin él, "Aprobar todo" con más de 200 pendientes mandaba la
     *   cola entera al bulk, el endpoint devolvía 400 y el toast decía "no se pudo guardar" — para
     *   siempre, porque reintentar mandaba lo mismo. Ahora resuelve la primera tanda y deja el
     *   resto en la cola: la confirmación del rail dice cuántos son los de ESTA tanda, y al
     *   terminar el rail sigue mostrando lo que queda.
     * - `MAX_MERGES_PER_BATCH` es el del fan-out de PATCH. Los hechos con duplicado quedan FUERA
     *   del bulk (aprobarlos a secas dejaría en la memoria el viejo y el nuevo diciendo lo mismo,
     *   que es lo que el usuario está resolviendo), y cada fusión son dos PATCH: sin tope, una
     *   tanda de 200 duplicados eran 400 requests de un saque.
     * - `MAX_CASCADE` es el de la animación: 200 cartas volando de a una son 22 segundos.
     *
     * Y los jobs corren EN SERIE (`runJobsInSequence`), no con `Promise.all`.
     */
    const handleApproveAll = useCallback(() => {
        if (exitingRef.current) return;
        const {plain, merges, order} = approveAllBatch;
        if (!order.length) return;
        const ids = plain.map(f => f.id);

        let i = 0;
        const finish = () => {
            setExiting(null);
            exitingRef.current = false;
            setHidden(order);
            // Un paso de sesión por JOB, con el tamaño del job: el bulk es UNA entrada de deshacer
            // de N hechos y cada fusión es la suya. Así un Z revierte un job y el rail resta
            // exactamente lo que ese job movió.
            const jobs = [];
            if (ids.length) jobs.push({n: ids.length, start: () => actions.approveAll(ids)});
            merges.forEach(({fact, duplicate}) => jobs.push({
                n: 1, start: () => actions.mergeAndApprove(fact.id, duplicate.id),
            }));
            // Cuántos hechos entraron DE VERDAD. Con 200 hechos hay varios jobs y no tienen por qué
            // correr la misma suerte: avisar "no se pudo guardar" porque uno de siete falló deja al
            // usuario sin saber qué pasó ni qué le quedó — y mirando una cola que ya no es la que él
            // vio. Una fusión a medias SÍ aprobó su hecho (el que quedó sin superseder es el
            // duplicado viejo), así que cuenta: sólo el `false` no movió nada.
            runJobsInSequence(jobs).then((entered) => {
                const left = order.length - entered;
                // Lo que no entró vuelve a mostrarse: el store ya revirtió su parte y dejar esas
                // cartas escondidas mostraría una cola vacía que no existe.
                if (left > 0) setHidden([]);
                if (!entered) {
                    toast.error(t('ia_booster.deck.error'));
                    return;
                }
                // `n de total`, no "quedaron {{left}}": el aviso tiene que ser gramatical con
                // cualquier número, y las variantes singular/plural son otras dos claves que
                // alguien va a olvidar de traducir.
                notifyRef.current(left > 0
                    ? t('ia_booster.deck.toast_approved_all_partial',
                        {n: entered, total: order.length})
                    : t('ia_booster.deck.toast_approved_all', {count: order.length}));
            });
        };
        const shown = Math.min(order.length, MAX_CASCADE);
        const step = () => {
            if (i >= shown) {
                timerRef.current = setTimeout(finish, CASCADE_MS);
                return;
            }
            setExiting({id: order[i], dir: 'right'});
            if (i > 0) {
                const gone = order.slice(0, i);
                setHidden(gone);
            }
            i += 1;
            timerRef.current = setTimeout(step, CASCADE_MS);
        };
        exitingRef.current = true;
        step();
    }, [actions, approveAllBatch, runJobsInSequence, t]);

    /**
     * Aprobar la tanda que ofreció el knob al subir la autonomía, con el MISMO ruteo que
     * «Aprobar todo».
     *
     * ⚠️ Hasta el review del 10/09 esto era `accept(actions.approveAll)`: los ids del backend iban
     * **crudos** al bulk. `handleApproveAll` parte la tanda a propósito —los hechos con duplicado
     * sugerido no pueden ir al bulk, porque aprobarlos a secas deja en la memoria el viejo y el
     * nuevo diciendo lo mismo— y la oferta se saltaba esa partición: con hechos así entre los N
     * ofrecidos, aceptar metía duplicados a la biblioteca. Los dos caminos que aprueban una tanda
     * tienen que rutear igual (hallazgo C-knob-12).
     *
     * No dibuja cascada: estas cartas pueden no estar en la pila (la oferta sale de la cola entera,
     * no del mazo), y el store las saca solo con su mutación optimista.
     */
    const approveOffered = useCallback((factIds) => {
        // El reparto vive en `offeredBatchOf`, afuera y puro: es donde se concentran C-knob-12 y
        // C-knob2-02, y adentro del componente no se podía testear.
        const {ids, merges} = offeredBatchOf(factIds, facts, similarOf);
        if (!ids.length && !merges.length) return Promise.resolve(false);
        const jobs = [];
        // Los que el backend no pudo aprobar porque envejecieron entre el GET de la oferta y este
        // POST. `skip_missing` es lo que evita que ESO tire la tanda entera (C-knob-08): sin él, un
        // solo hecho borrado en otra pestaña convertía «aprobar 45» en 45 sin aprobar.
        let skipped = 0;
        // ⚠️ Si el BULK se rechazó pero alguna fusión entró, `entered > 0` y la tanda parecía haber
        // contestado: el aviso decía «Aprobados: 2 de 45» y no salía ningún error, así que la falla
        // del bulk —los 43 hechos que no se movieron— era indistinguible de 43 que envejecieron
        // (C-knob3-08, review del 27/09). El job lo anota al resolver.
        let bulkRejected = false;
        if (ids.length) {
            jobs.push({
                n: ids.length,
                start: () => actions.approveAll(ids, {
                    skipMissing: true,
                    onSkipped: (n) => { skipped += n; },
                }).then((ok) => {
                    if (ok === false) bulkRejected = true;
                    return ok;
                }),
                // Lo que de verdad se movió, para el paso de sesión y para el aviso (C-knob2-01).
                real: () => Math.max(0, ids.length - skipped),
            });
        }
        merges.forEach(({fact, duplicate}) => jobs.push({
            n: 1, start: () => actions.mergeAndApprove(fact.id, duplicate.id),
        }));
        return runJobsInSequence(jobs).then((entered) => {
            // ⚠️ **Primero se decide si el backend CONTESTÓ.** Avisar por `entered < factIds.length` a
            // secas confundía «entraron menos» con «no entró nada»: con la tanda rechazada entera
            // (`entered` y `skipped` en 0) salía «Aprobados: 0 de 3» junto al error, y su «Deshacer»
            // —que revierte el último paso de la sesión— revertía la decisión ANTERIOR del usuario,
            // que no tenía nada que ver con la oferta (QA-knob2-05, 27/09). Un bulk rechazado no
            // mueve nada, así que tampoco tiene nada que deshacer: el único aviso es el error, y lo
            // da `accept` al recibir el `false`.
            const answered = entered > 0 || skipped > 0;
            if (!answered) return false;
            // Lo que no entró se NOMBRA: el modal prometió N y callarse la diferencia es el mismo
            // silencio que el review vino a cerrar. `entered` ya viene descontado de lo que el
            // BACKEND dijo que salteó (ver `job.real`), así que no hay que volver a restarlo acá
            // —hacerlo lo contaba dos veces—. Y el texto es el de la OFERTA, no el de «Aprobar
            // todo»: acá lo que no entró no "sigue en la cola" (al salteado lo resolvió otra
            // persona mientras el modal estaba abierto), así que el aviso no afirma dónde quedó.
            // El éxito total también avisa, con la misma clave que la acción hermana: el modal se
            // cerraba en silencio y el Deshacer quedaba sólo en `Ctrl+Z` (QA-VIS-knob2-06).
            // El «Deshacer» sólo si ESTA acción movió algo: con todos salteados el store no dejó
            // entrada que revertir y el botón caería sobre la decisión anterior.
            notifyRef.current(entered < factIds.length
                ? t('memory_autonomy.raise_offer_partial', {n: entered, total: factIds.length})
                : t('ia_booster.deck.toast_approved_all', {count: entered}), entered > 0);
            // …y si además el bulk FALLÓ, se dice: el parcial cuenta lo que entró, pero no distingue
            // «el resto envejeció» de «el resto no se pudo guardar», y eso cambia qué tiene que hacer
            // el usuario. El aviso de la tanda se queda con su Deshacer para lo que sí entró.
            if (bulkRejected) toast.error(t('ia_booster.deck.error'));
            // La oferta se cierra aunque el backend haya movido 0: que TODOS los hechos hayan
            // envejecido no es un fallo —la cola quedó como el usuario quería— y lo explica el aviso.
            return true;
        });
    }, [actions, facts, runJobsInSequence, similarOf, t]);

    /**
     * Descartar una familia de ruido: UN request, no N.
     *
     * El store SÍ expone el rechazo en lote (`rejectAll`, que va al mismo `bulkReviewFacts` que
     * "Aprobar todo"). Con N rechazos en paralelo, 40 hechos eran 40 requests, 40 filas en la
     * Bitácora y 40 encolados de embedding —justo lo que el endpoint bulk existe para evitar—, y
     * además 40 entradas de deshacer contra un solo paso de sesión.
     *
     * El tope de la tanda es el mismo del backend: la sección de ruido ya recorta el grupo a
     * `MAX_BULK` para que su confirmación diga el número real, y acá se vuelve a acotar por si
     * llegara otra cosa.
     */
    const handleDiscardNoise = useCallback((ids) => {
        const batch = ids.slice(0, MAX_BULK);
        if (!batch.length) return;
        runStep('noise', batch.length, actions.rejectAll(batch)).then((ok) => {
            if (!ok) {
                toast.error(t('ia_booster.deck.error'));
                return;
            }
            notifyRef.current(t('ia_booster.deck.toast_rejected_group', {n: batch.length}));
        });
    }, [actions, runStep, t]);

    // --- teclado -----------------------------------------------------------------------------------

    useEffect(() => {
        if (!keyboardActive) return undefined;
        const onKeyDown = (e) => {
            const focused = document.activeElement;
            // Un campo de TEXTO se lleva todo, el deshacer incluido (ahí Ctrl+Z es el del navegador).
            if (isTextEntry(focused)) return;
            // Un modal abierto se lleva TODO el teclado. Sin esto, contestar "¿seguro?" con el
            // teclado aprueba de paso las cartas que quedaron atrás del modal.
            if (document.body.classList.contains('modal-open')) return;
            const key = e.key || '';
            const lower = key.toLowerCase();

            // Deshacer vive en los dos modos: es la red, y la red no depende de cómo estés mirando.
            //
            // Pero va CON modificador. Una `z` pelada revierte la última decisión estando el foco
            // donde esté —un nub, el botón del rail, cualquier cosa del acto que no sea un campo de
            // texto—, y ninguna de esas superficies avisa que esa tecla hace algo. `Ctrl/⌘+Z` es
            // además lo que el usuario ya tiene aprendido para deshacer. `Shift` queda afuera a
            // propósito: `Ctrl+Shift+Z` es REHACER en todos lados, y acá no hay rehacer — mandar otro
            // deshacer sería lo contrario de lo que el usuario pidió.
            if (lower === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
                if (undoDepthRef.current > 0) {
                    e.preventDefault();
                    handleUndo();
                }
                return;
            }
            // De acá para abajo van las teclas de la CARTA, y esas sí se las lleva cualquier control
            // que las use: con el foco en el riel del knob, ← y → son del riel.
            if (isTypingTarget(focused)) return;
            if (mode !== MODE_DECK || editingId || !top) return;
            // La carta anterior todavía está volando: esta decisión NO se aplica. Antes se
            // descartaba en silencio — encadenando `→` y `←` la segunda no hacía nada y el usuario
            // no recibía ninguna señal de que su gesto se había ignorado.
            if (exitingRef.current) {
                if (key === 'ArrowRight' || key === 'ArrowLeft' || lower === 'e') {
                    e.preventDefault();
                    signalIgnored();
                }
                return;
            }
            const duplicate = similarOf(top);
            if (key === 'ArrowRight') {
                e.preventDefault();
                if (duplicate) handleMerge(top, duplicate);
                else handleApprove(top);
            } else if (key === 'ArrowLeft') {
                e.preventDefault();
                handleReject(top);
            } else if (lower === 'e') {
                e.preventDefault();
                setEditingId(top.id);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [editingId, handleApprove, handleMerge, handleReject, handleUndo, keyboardActive, mode,
        signalIgnored,
        similarOf, top]);

    // --- arrastre: los nubs se encienden sin re-renderizar la pila ---------------------------------

    const handleDragMove = useCallback((dx) => {
        if (nubNoRef.current) nubNoRef.current.classList.toggle('-lit', dx < -NUB_FROM);
        if (nubOkRef.current) nubOkRef.current.classList.toggle('-lit', dx > NUB_FROM);
    }, []);

    // --- render ------------------------------------------------------------------------------------

    if (loading) {
        return (
            <div className="iabr-deck" ref={rootRef}>
                <div className="box-loader"><Spinner color="positive"/></div>
            </div>
        );
    }

    if (loadFailed) {
        return (
            <div className="iabr-deck" ref={rootRef}>
                {/* ⚠️ El error tiene su propio modificador. Compartía la caja del vacío, así que
                    «No se pudieron cargar los hechos» salía en el MISMO verde y el mismo peso que
                    «Todo revisado» —un error pintado como éxito— y sin ícono ni explicación de qué
                    hacer (QA-VIS-knob2-13, 27/09). */}
                <div className="iabr-empty -error">
                    <FaIcon iconName="triangle-exclamation" className="iabr-empty-icon"/>
                    <p className="iabr-empty-title">{t('ia_booster.deck.load_error')}</p>
                    <p className="iabr-empty-text">{t('ia_booster.deck.load_error_text')}</p>
                    <button type="button" className="iabr-btn -ghost" onClick={actions.refetch}>
                        {t('ia_booster.deck.retry')}
                    </button>
                </div>
            </div>
        );
    }

    const done = session.approved + session.rejected;
    // "Todo revisado" sólo se puede afirmar si hubo algo que revisar. Con la memoria en cero es una
    // felicitación por un trabajo que nadie hizo, y esconde lo único accionable: cargar material.
    const everReviewed = done > 0 || library.length > 0;

    const renderEmpty = () => extractionActive || processingUnknown ? (
        <div className="iabr-empty -processing" role="status" aria-live="polite">
            <FaIcon iconName="clock" className="iabr-empty-icon"/>
            <p className="iabr-empty-title">{t(extractionActive
                ? 'ia_booster.deck.processing_title' : 'ia_booster.deck.processing_unknown')}</p>
            <p className="iabr-empty-text">{t('ia_booster.deck.processing_text')}</p>
        </div>
    ) : (
        <div className="iabr-empty">
            <FaIcon iconName={everReviewed ? 'circle-check' : 'cloud-arrow-up'}
                    className="iabr-empty-icon"/>
            <p className="iabr-empty-title">
                {t(everReviewed ? 'ia_booster.deck.empty_done_title' : 'ia_booster.deck.empty_never_title')}
            </p>
            <p className="iabr-empty-text">
                {t(everReviewed ? 'ia_booster.deck.empty_done_text' : 'ia_booster.deck.empty_never_text')}
            </p>
            <div className="iabr-empty-actions">
                {everReviewed ? (
                    // El `href` es el que hace que esto sea un link de verdad (se puede abrir en otra
                    // pestaña, se anuncia como enlace y funciona sin JS). Pero cuando hay handler hay
                    // que frenar el salto del navegador: si no, el ancla salta de golpe al acto
                    // Memoria y ENCIMA corre el scroll suave del contenedor — dos desplazamientos
                    // para un click, y el suave arrancando desde donde el otro ya lo dejó.
                    <a className="iabr-btn -primary" href={`#${BOOSTER_STEP_MEMORY}`}
                       onClick={onGoToMemory ? (e) => { e.preventDefault(); onGoToMemory(e); } : undefined}>
                        {t('ia_booster.deck.empty_done_cta')}
                    </a>
                ) : (
                    <a className="iabr-btn -primary" href={`#${BOOSTER_STEP_FEED}`}>
                        {t('ia_booster.deck.empty_never_cta')}
                    </a>
                )}
            </div>
        </div>
    );

    return (
        <div className={`iabr-deck ${ignoredAt ? '-ignored' : ''}`} ref={rootRef}
             data-client={clientId}>
            <div className="iabr-modeswitch" role="group"
                 aria-label={t('ia_booster.deck.mode_label')}>
                <button type="button" ref={deckModeRef}
                        className={`iabr-msw ${mode === MODE_DECK ? '-on' : ''}`}
                        aria-pressed={mode === MODE_DECK} onClick={() => changeMode(MODE_DECK)}>
                    {t('ia_booster.deck.mode_one')}
                    {/* La estrella del mockup: por qué el mazo es lo recomendado, al pasar el
                        mouse. Decorativa para el lector de pantalla — la razón está en el rail. */}
                    <span className="iabr-msw-star" aria-hidden="true">
                        ★<span className="iab-tip">{t('ia_booster.deck.mode_one_tip')}</span>
                    </span>
                </button>
                <button type="button" className={`iabr-msw ${mode === MODE_LIST ? '-on' : ''}`}
                        aria-pressed={mode === MODE_LIST} onClick={() => changeMode(MODE_LIST)}>
                    {t('ia_booster.deck.mode_list')}
                </button>
                {/* El tercer modo aparece SÓLO si hay ruido: es lo que pidió fede el 10/09 («que
                    esté dentro del mismo componente... y quizás puede haber ahí un ítem más que
                    sea ruido probable en caso de que exista al menos uno»). Un botón permanente
                    que casi siempre lleva a un panel vacío le enseña al usuario a no mirarlo.
                    El conteo va acá y no adentro del panel: es lo que decide si vale la pena
                    entrar, y adentro el número ya lo dice cada familia. */}
                {noiseQueue.length > 0 && (
                    <button type="button" className={`iabr-msw ${mode === MODE_NOISE ? '-on' : ''}`}
                            aria-pressed={mode === MODE_NOISE} onClick={() => changeMode(MODE_NOISE)}>
                        {t('ia_booster.deck.noise_title')}
                        <span className="iabr-msw-count">{noiseQueue.length}</span>
                    </button>
                )}
            </div>

            <div className="iabr-mazo">
                <div className="iabr-mazo-main">
                    {/* El vacío es el de la cola PRINCIPAL. En el modo ruido no corresponde: la
                        cola principal puede estar terminada y el ruido seguir teniendo material,
                        y «¡Listo, no queda nada!» arriba de siete hechos es una contradicción. */}
                    {visible.length === 0 && mode !== MODE_NOISE && renderEmpty()}

                    {visible.length > 0 && mode === MODE_DECK && (
                        <>
                            <div className="iabr-arena">
                                <div className="iabr-deckzone">
                                    {stack.map((fact, i) => (
                                        <ReviewCard
                                            key={fact.id}
                                            fact={fact}
                                            pos={i}
                                            documentsById={documentsById}
                                            similar={similarOf(fact)}
                                            similarKind={similarKindOf(fact)}
                                            busy={isBusy(fact.id)}
                                            interactive={i === 0 && !deckEditing && !exiting}
                                            exiting={exiting && exiting.id === fact.id ? exiting.dir : null}
                                            editing={editingId === fact.id}
                                            onSwipe={(dir) => (dir === 'right'
                                                ? (similarOf(fact)
                                                    ? handleMerge(fact, similarOf(fact))
                                                    : handleApprove(fact))
                                                : handleReject(fact))}
                                            onDragMove={handleDragMove}
                                            onEdit={f => setEditingId(f.id)}
                                            onCancelEdit={() => setEditingId(null)}
                                            onSaveEdit={handleSaveEdit}
                                            onMerge={handleMerge}
                                            onSplit={handleSplit}
                                            onOpenSource={onOpenSource}
                                        />
                                    ))}
                                </div>
                                <button type="button" className="iabr-nub -no" ref={nubNoRef}
                                        disabled={!top || deckEditing}
                                        onClick={() => handleReject(top)}>
                                    <FaIcon iconName="arrow-left"/>
                                    <span>{t('ia_booster.deck.reject')}</span>
                                </button>
                                <button type="button" className="iabr-nub -ok" ref={nubOkRef}
                                        disabled={!top || deckEditing}
                                        onClick={() => (similarOf(top)
                                            ? handleMerge(top, similarOf(top))
                                            : handleApprove(top))}>
                                    <span>
                                        {similarOf(top)
                                            ? t('ia_booster.deck.merge_approve')
                                            : t('ia_booster.deck.approve')}
                                    </span>
                                    <FaIcon iconName="arrow-right"/>
                                </button>
                            </div>
                            <p className="iabr-keyhint">
                                <span>{t('ia_booster.deck.keyhint_swipe')}</span>
                                <span>{t('ia_booster.deck.keyhint_edit')}</span>
                                <span>{t('ia_booster.deck.keyhint_undo')}</span>
                            </p>
                        </>
                    )}

                    {visible.length > 0 && mode === MODE_LIST && (
                        <ReviewList
                            queue={visible}
                            documentsById={documentsById}
                            onOpenSource={onOpenSource}
                            similarById={similarById}
                            hintKindById={hintKindById}
                            isBusy={isBusy}
                            editingId={editingId}
                            onApprove={handleApprove}
                            onReject={handleReject}
                            onEdit={f => setEditingId(f.id)}
                            onCancelEdit={() => setEditingId(null)}
                            onSaveEdit={handleSaveEdit}
                        />
                    )}

                    {mode === MODE_NOISE && (
                        <NoiseQueueSection
                            noise={noiseQueue}
                            documentsById={documentsById}
                            onOpenSource={onOpenSource}
                            isBusy={isBusy}
                            editingId={editingId}
                            maxPerBatch={MAX_BULK}
                            onApprove={approveNow}
                            onReject={rejectNow}
                            onEdit={f => setEditingId(f.id)}
                            onCancelEdit={() => setEditingId(null)}
                            onSaveEdit={handleSaveEditNow}
                            onDiscardGroup={handleDiscardNoise}
                        />
                    )}
                </div>

                {/* El universo del rail es la cola PRINCIPAL: las cartas que esperan una decisión
                    de a una. El ruido probable queda afuera del número y de la barra —sus descartes
                    por grupo se cuentan aparte (`session.noise`) justamente para que no inflen el
                    avance: 40 ruidos descartados de un click con 3 cartas en el mazo daban
                    `done=40 / total=43`, un 93 % sin haber decidido nada—.

                    🔴 `pendingCount` del store, NO `visible.length`. `visible` le resta `hidden`,
                    que son las cartas que la cascada de «Aprobar todo» ya hizo volar pero que el
                    bulk todavía no confirmó: durante esa ventana el rail decía un número y el HUD
                    de arriba otro sobre la misma cola. Es lo que Pablo vio el 08/09 («5 esperando»
                    y «4 esperando» en la misma pantalla). El rail es el que tenía que ceder: lo que
                    todavía no confirmó el backend SIGUE pendiendo, y la pila que se vacía ya cuenta
                    esa historia sola. `approveAllCount` sí es sólo del mazo —es lo que el botón
                    resuelve— y sale de la MISMA tanda que se va a ejecutar (`approveAllBatch`),
                    acotada por los dos topes: el del bulk y el de las fusiones. */}
                <ReviewRail
                    showShortcut={mode !== MODE_NOISE}
                    pending={pendingCount}
                    approved={session.approved}
                    rejected={session.rejected}
                    approveAllCount={approveAllBatch.order.length}
                    busy={!!exiting}
                    onApproveAll={handleApproveAll}
                    autonomy={autonomy}
                    onAutonomyChange={handleAutonomyChange}
                    autonomySaving={autonomySaving}
                    autonomyKnobRef={knobRef}
                />
            </div>

            {/* «Subiste la autonomía: hay N esperando que se pueden aprobar solos, ¿los apruebo?»
                (card 8uu3IdNd). Aprobar va por el store y no por la API pelada: así la tanda entra
                a la pila de Deshacer y a los contadores de la sesión igual que si el usuario
                hubiera apretado «Aprobar todo» — y desde el review del 10/09, por el MISMO ruteo de
                fusiones (`approveOffered`), no por `approveAll` a secas. */}
            <AutonomyRaiseOfferModal
                offer={autonomyOffer.offer}
                busy={autonomyOffer.busy}
                onYes={() => autonomyOffer.accept(approveOffered)
                    // Sólo si la oferta se CERRÓ: cuando el bulk falla el modal queda abierto para
                    // reintentar, y mandar el foco al riel lo sacaría de ahí.
                    .then((closed) => { if (closed) refocusKnob(); })}
                onNo={() => { autonomyOffer.dismiss(); refocusKnob(); }}
            />

            {/* Los pares que el motor de duplicados no pudo cerrar solo (Dicen lo mismo / Cambió /
                Se contradicen) y las fusiones automáticas con su Deshacer. Colapsado, con su propio
                número: es de la etapa de revisión, no del archivo (Pablo, 10/09), y no entra al «N
                por revisar». Lee el store; no calcula nada. */}
            <DuplicatesSection/>

            {/* Lo que el triage archivó solo: rechazado sin persona, recuperable. Colapsado, fuera
                del mazo y de todos los números. */}
            <ArchivedNoiseSection
                facts={archivedQueue}
                failed={archivedFailed}
                hidden={archivedHidden}
                documentsById={documentsById}
                onOpenSource={onOpenSource}
                isBusy={isBusy}
                onRestore={restoreNow}
            />

            {/* El crudo de la fuente de una carta ya no se monta acá: lo abre el visor único de la
                página (`SourceViewerHost`, en `BoosterFlowContent`). Mientras está abierto,
                `body.modal-open` apaga el teclado del mazo (la guarda de arriba), así que Escape lo
                cierra sin rechazar nada. */}
        </div>
    );
}

export default BoosterReviewDeck;
